import type React from 'react'
import styles from './MetroStationHeader.module.css'

export interface MetroStationHeaderProps {
  /** Display name of the station. */
  title: string
  /** Line name subtitle (e.g. "Bakerloo line"). `undefined` hides the paragraph. */
  subtitle: string | undefined
  /** Zone label (typically "1", "2", etc.). `null` hides the badge. */
  badge: string | null
  /** Line color as CSS hex (e.g. "#B36305"). Applied to the line-chip border. */
  lineHexColor: string
  /** Trailing "zone" label under the badge number. @defaultValue `'zone'` */
  zoneLabel?: string
  /** Zone-badge accessible label template. @defaultValue ``(b) => `Zone ${b}` `` */
  zoneAria?: (badge: string) => string
  /**
   * Opt-in single-line title: truncate the station name to ONE line with an
   * ellipsis. Default `false` keeps the 2-line `-webkit-box` clamp used by
   * StationDetails. StationCard (compact card, 2026-07) opts in. Mirrors the
   * iOS single-line card-title variant. @defaultValue `false`
   */
  singleLineTitle?: boolean
  /**
   * Turns the subtitle into a link to the line's view on the map
   * (`/<locale>/lines/<slug>`, Figma LineView E): "Bakerloo line ›" in the
   * line colour. `undefined` keeps plain text.
   */
  subtitleHref?: string
  /**
   * In-app handling of a subtitle-link click (opens the line view without
   * a page load); the href stays for new-tab / crawler use.
   */
  onSubtitleTap?: () => void
}

/**
 * Metro-frontend's local override of the design-kit StationHeader —
 * Figma SD/StationHeaderV2: 48×48 line chip with a 7.5px stroke, the iOS
 * typography tokens and zone badge. Used by StationCardView,
 * StationDetails and the line view (line panel and card), which is why
 * the subtitle can also be a link to the line's view.
 *
 * The component API is the same shape as design-kit.StationHeader so
 * the swap is local to import statements; tests and e2e selectors
 * reading `data-testid="station-header"`, `station-header-title`,
 * `line-chip`, and `zone-badge` continue to pass.
 */
export function MetroStationHeader({
  title,
  subtitle,
  badge,
  lineHexColor,
  zoneLabel = 'zone',
  zoneAria = (b: string) => `Zone ${b}`,
  singleLineTitle = false,
  subtitleHref,
  onSubtitleTap,
}: MetroStationHeaderProps): React.ReactElement {
  const titleClass = singleLineTitle ? `${styles.title} ${styles.titleSingleLine}` : styles.title
  return (
    <header className={styles.root} data-testid="station-header">
      <span
        className={styles.lineChip}
        style={{ borderColor: lineHexColor, color: lineHexColor }}
        aria-hidden="true"
        data-testid="line-chip"
      />
      <div className={styles.textColumn}>
        <h1 className={titleClass} data-testid="station-header-title">
          {title}
        </h1>
        {subtitle !== undefined && (
          <p className={styles.subtitle}>
            {subtitleHref === undefined ? (
              subtitle
            ) : (
              <a
                className={styles.subtitleLink}
                href={subtitleHref}
                style={{ color: lineHexColor }}
                data-testid="station-header-line-link"
                onClick={(e) => {
                  if (onSubtitleTap === undefined) return
                  e.preventDefault()
                  onSubtitleTap()
                }}
              >
                {subtitle}{' '}
                <span className={styles.subtitleLinkChevron} aria-hidden="true">
                  ›
                </span>
              </a>
            )}
          </p>
        )}
      </div>
      {badge !== null && (
        <span className={styles.zoneBadge} aria-label={zoneAria(badge)} data-testid="zone-badge">
          <span className={styles.zoneBadgeNumber}>{badge}</span>
          <span className={styles.zoneBadgeLabel}>{zoneLabel}</span>
        </span>
      )}
    </header>
  )
}
